import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RefundStatusCounts } from '../../hooks';
import type { Filters } from '../../models/Filters';

type RefundStatus = NonNullable<Filters['status']>;

interface RefundsStatusToolbarProps {
  activeStatus: RefundStatus;
  onStatusChange: (status: RefundStatus) => void;
  statusCounts?: RefundStatusCounts;
  isLoadingCounts?: boolean;
}

export const RefundsStatusToolbar = ({
  activeStatus,
  onStatusChange,
  statusCounts,
  isLoadingCounts = false,
}: RefundsStatusToolbarProps) => {
  const { t } = useTranslation('refundsModule');

  const toolbarButtons = useMemo(
    () => [
      { key: '', countKey: 'all', label: t('toolbar.all') },
      { key: 'pending', countKey: 'pending', label: t('toolbar.pending') },
      { key: 'liquidated', countKey: 'liquidated', label: t('toolbar.liquidated') },
    ],
    [t],
  );

  const formatCount = (count: number | undefined): string => {
    if (count === undefined) return '';
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return String(count);
  };

  return (
    <div
      className="flex bg-card-bg rounded-lg text-sm p-1 mb-4 overflow-x-auto scrollbar-hide light:border-card-border light:border"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {toolbarButtons.map((button) => {
        const isActive = activeStatus === button.key;
        const countText = formatCount(statusCounts?.[button.countKey]);

        return (
          <a
            key={button.key}
            href="#"
            onClick={(event) => {
              event.preventDefault();
              onStatusChange(button.key as RefundStatus);
            }}
            className={`px-3 py-1 rounded hover:bg-hover-press whitespace-nowrap flex items-center gap-1.5 ${
              isActive ? 'bg-hover-press' : ''
            }`}
            data-testid={`refunds-status-tab-${button.key || 'all'}`}
          >
            <span>{button.label}</span>
            {countText && (
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                  isActive ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                } ${isLoadingCounts ? 'animate-pulse' : ''}`}
              >
                {countText}
              </span>
            )}
          </a>
        );
      })}
    </div>
  );
};
